import { useCallback, useEffect, useMemo } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import {
  CALENDAR_QUERY_KEY_PREFIX,
  SALES_CYCLES_QUERY_KEY_PREFIX,
  findBarVersion,
  isEntryOutdated,
} from './freshness';

/**
 * React Query wiring for the acting-user freshness rules of task 22.7.
 *
 * The pure comparison and cache lookup live in `freshness.ts`; this module holds
 * the two hooks that couple them to the query cache. Both invalidate by the
 * shared `['calendar']` prefix rather than a specific filter signature, because
 * the acting user's calendar may hold any filter state and every rendering of the
 * changed Initiative must refresh (design.md, "Freshness").
 */

/**
 * Returns a callback that invalidates the acting user's calendar so an own save
 * appears within 3 s without a manual reload (R13.3).
 *
 * A save can move an Initiative on the calendar (dates, Platform, status,
 * finality, priority) and can change the Sales_Cycle bands the Sales_Cycle
 * orientation renders, so both the calendar bars/count/legend query and the
 * year's Sales_Cycle definitions are invalidated. Invalidation, not an optimistic
 * patch: the server owns the rendered values and the count, and re-reading it is
 * what keeps the screen, the count and a subsequent export in agreement
 * (R13.7, R13.8, Property 8). The mutation stays responsible for its own error
 * handling; this only refreshes the read on success.
 */
export function useInvalidateCalendar(): () => void {
  const client = useQueryClient();
  return useCallback(() => {
    void client.invalidateQueries({ queryKey: [CALENDAR_QUERY_KEY_PREFIX] });
    void client.invalidateQueries({ queryKey: [SALES_CYCLES_QUERY_KEY_PREFIX] });
  }, [client]);
}

/** The outcome of comparing a Detail_Panel read against the calendar bar it opened from. */
export interface CalendarEntryFreshness {
  /**
   * True when the Detail_Panel read a record version later than the one the
   * calendar bar carried — the bar is showing stale values (R13.5). The
   * Detail_Panel surfaces this as the "outdated" indication; the hook has already
   * asked the calendar to re-render from current values.
   */
  readonly isOutdated: boolean;
}

/**
 * Compares the version the Detail_Panel read for `initiativeId` against the
 * version the calendar bar carried, and — when the detail is later — marks the
 * entry outdated and re-renders it from current values within 3 s (R13.5).
 *
 * The bar version is recovered from the calendar cache ({@link findBarVersion}),
 * so the caller supplies only the detail's `version`; passing `null` while the
 * detail is still loading yields `isOutdated: false` and triggers nothing, so the
 * indicator never flashes during the open. When the detail version exceeds the
 * bar's, the calendar key is invalidated so the stale bar re-reads and re-renders
 * from the current stored values, satisfying the "re-render within 3 s" half of
 * R13.5 alongside the visible indication the panel shows.
 *
 * This is the seam task 23.4 fills: 23.4's Detail_Panel content read passes its
 * fetched `detailVersion` here, and the returned `isOutdated` drives the
 * indicator. Until then the panel can pass `null` and the hook is inert.
 */
export function useCalendarEntryFreshness(
  initiativeId: string,
  detailVersion: number | null,
): CalendarEntryFreshness {
  const client = useQueryClient();

  const barVersion = useMemo(
    () => (initiativeId === '' ? null : findBarVersion(client, initiativeId)),
    // `detailVersion` is included so the bar version is re-read once the detail
    // resolves, catching the bar the currently mounted grid rendered rather than
    // a value captured before the detail's fetch settled.
    [client, initiativeId, detailVersion],
  );

  const isOutdated = isEntryOutdated(barVersion, detailVersion);

  useEffect(() => {
    if (!isOutdated) {
      return;
    }
    // The bar predates a persisted change: re-render it from current values by
    // invalidating the calendar read. The Sales_Cycle bands can move with the
    // same change, so refresh their definitions too.
    void client.invalidateQueries({ queryKey: [CALENDAR_QUERY_KEY_PREFIX] });
    void client.invalidateQueries({ queryKey: [SALES_CYCLES_QUERY_KEY_PREFIX] });
  }, [client, isOutdated]);

  return { isOutdated };
}
